import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import Breadcrumb from "../Pages/Breadcrumb";
import "./Courseplan.css";
import Image from "../ui/Image";
import { useLanguage } from "../../store/language-context";
import { useTranslation } from "react-i18next";

const years = [2025, 2024, 2023, 2022, 2021];

const TrainingPlan = () => {
  const { t } = useTranslation();
  const [categories, setCategories] = useState([]);
  const [selectedYear, setSelectedYear] = useState(2025);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState({}); // Track loading state for each category

  const { URL } = useLanguage();
  // Fetch categories from the API
  const fetchCategories = async (year = selectedYear, page = 1) => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${URL}/categories?year=${year}&page=${page}&per_page=6`
      );

      const { data } = response.data;
      let categoriesData = data?.data || [];

      if (Array.isArray(categoriesData)) {
        categoriesData = categoriesData.sort((a, b) => {
          if (a.title === "default") return 1;
          if (b.title === "default") return -1;
          return 0;
        });

        setCategories(categoriesData);
        setTotalPages(data.meta.last_page);
        setCurrentPage(data.meta.current_page);
      } else {
        throw new Error("Invalid data format from API");
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      setError("حدث خطأ غير متوقع حاول مره اخرى");
    } finally {
      setLoading(false);
    }
  };

  // Handle year change from the dropdown
  const handleYearChange = (year) => {
    setSelectedYear(year);
    setIsDropdownOpen(false);
    setCurrentPage(1); // Reset to the first page when year changes
  };

  // Handle page change for pagination
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Handle file download
  const handleDownload = async (categoryId, categoryName) => {
    try {
      setDownloadLoading((prevState) => ({ ...prevState, [categoryId]: true }));

      const downloadResponse = await axios.get(
        `${URL}/categories/export/${categoryId}`
      );

      const { download_link } = downloadResponse.data.data;

      const fileExtension = 'xlsx';
      const fileName = `${t('routes.training-plan-single')}.${fileExtension}`;

      const link = document.createElement("a");
      link.href = download_link;
      link.download = fileName;
      link.click();

      setDownloadLoading((prevState) => ({ ...prevState, [categoryId]: false }));
    } catch (error) {
      console.error("Error fetching download link:", error);
      setError(t('custom.err'));
      setDownloadLoading((prevState) => ({ ...prevState, [categoryId]: false }));
    }
  };

  useEffect(() => {
    fetchCategories(selectedYear, currentPage);
  }, [selectedYear, currentPage, URL]);

  useEffect(() => {
    fetchCategories(selectedYear);
  }, [selectedYear, URL]);

  return (
    <>
      <Breadcrumb pages={[t('routes.training-plan')]} />
      <div className="container mx-auto p-6">
        {/* Year Selection Dropdown */}
        <div className="relative w-64 mb-6">
          {isDropdownOpen && (
            <ul className="absolute w-full mt-2 bg-white border border-gray-300 rounded-lg z-50">
              {years.map((year) => (
                <li
                  key={year}
                  onClick={() => handleYearChange(year)}  // Use handleYearChange here
                  className={`p-3 cursor-pointer text-gray-800 !text-start hover:bg-gray-100 ${selectedYear === year ? "bg-gray-200 font-bold" : ""}`}
                >
                  {t('routes.training-plan')} {year}
                </li>
              ))}
            </ul>
          )}
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="w-full h-48 l-load relative rounded-lg">
                <div className="absolute bottom-4 right-4">
                  <div className="w-28 h-3 rounded-lg l-load-dt"></div>
                  <div className="w-52 h-4 rounded-lg l-load-dt mt-2 mb-5"></div>
                  <div className="w-16 h-3 rounded-lg l-load-dt"></div>
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <p className="text-red-500 text-center">{error}</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {categories.length > 0 ? (
              categories.map((category, index) => (
                <div key={index} className="relative rounded-lg overflow-hidden border border-gray-200">
                  {/* Background Image */}
                  <div className="w-full h-48 rounded-lg relative">
                    <Image
                      src={category.image || 'https://ivorytraining.com/TrainingPlan/HpWyAyoovWza2p95CdnfYX9clQKB1NODK86Gisjl.jpg'}
                      alt={category.title}
                      className={'w-full h-full rounded-lg object-cover'}
                    />
                  </div>

                  {/* Overlay */}
                  <div className="absolute inset-0 overlay-card"></div>

                  {/* Text Content */}
                  <div className="absolute bottom-3 w-full flex flex-col justify-between px-4 text-white">
                    <div className="text-secondary-tw !text-start text-sm">
                      {t('routes.training-plan-single')} {selectedYear}
                    </div>
                    <p className="!text-start text-lg">{category.title}</p>

                    {/* Action Buttons */}
                    <div className="flex justify-between mt-2">
                      <div className="mt-1 pt-2 flex justify-between w-full border-t border-t-gray-200">
                        <Link to={`/training-plan/${category.id}`} className="!underline text-secondary-tw">
                          {t('custom.show')}
                        </Link>
                        {/* Updated Download Button */}
                        <button
                          onClick={() => handleDownload(category.id, category.title)}
                          className="!underline text-secondary-tw"
                          disabled={downloadLoading[category.id]}  // Disable the button while loading
                        >
                          {downloadLoading[category.id] ? (
                            <span>{t('custom.downloading')}...</span> // Show loading text
                          ) : (
                            t('custom.download')
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-center col-span-3"></p>
            )}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center mt-6 pagination flex-wrap">
            <button
              onClick={() => handlePageChange(currentPage - 1)}  // Use handlePageChange here
              disabled={currentPage === 1}
              className={`px-3 py-1 border rounded text-sm flex items-center justify-center ${currentPage === 1 ? 'opacity-50 cursor-not-allowed bg-[#e5e7eb]' : 'hover:bg-gray-200 bg-white text-gray-700'} m-1`}
            >
              <ArrowRight size={16} />
            </button>

            {[...Array(totalPages).keys()].map((num) => {
              const page = num + 1;
              const isActive = page === currentPage;
              return (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}  // Use handlePageChange here
                  className={`px-3 py-1 border rounded text-sm m-1 ${isActive ? 'bg-[#6a2f38] text-white' : 'bg-white text-gray-700 hover:bg-gray-200'}`}
                >
                  {page}
                </button>
              );
            })}

            <button
              onClick={() => handlePageChange(currentPage + 1)}  // Use handlePageChange here
              disabled={currentPage === totalPages}
              className={`px-3 py-1 border rounded text-sm flex items-center justify-center ${currentPage === totalPages ? 'opacity-50 cursor-not-allowed bg-[#e5e7eb]' : 'hover:bg-gray-200 bg-white text-gray-700'} m-1`}
            >
              <ArrowLeft size={16} />
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default TrainingPlan;